/**
 * Wave 29 Chunk 2 — SimplePay sync + match prompt (HR-only).
 *
 * Full-page flow:
 *  1. Status card with last-sync timestamps + counts.
 *  2. Action buttons: Pull employees / Pull payslips.
 *  3. Filter tabs (Unmatched / Matched / Ignored) + list with
 *     per-row Accept / Override / Ignore controls. The user picker
 *     for "override" deep-binds an arbitrary SF user to an SP employee.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronLeft, CloudDownload, Loader2, CheckCircle2, X, Users,
  ShieldAlert, RefreshCw, Link2, Unlink, Search,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";

const TABS = [
  { value: "unmatched", label: "Unmatched", testId: "tab-unmatched" },
  { value: "matched", label: "Matched", testId: "tab-matched" },
  { value: "ignored", label: "Ignored", testId: "tab-ignored" },
];

export default function SimplePaySync() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isHr = user && ["owner", "admin"].includes(user.role);
  const [status, setStatus] = useState(null);
  const [tab, setTab] = useState("unmatched");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [picker, setPicker] = useState(null); // {spid, options[]}

  useEffect(() => {
    if (user && !isHr) nav("/", { replace: true });
  }, [user, isHr, nav]);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/team/simplepay/status");
      setStatus(data);
    } catch (_e) { /* noop */ }
  };
  const loadRows = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/team/simplepay/matches?status=${tab}`);
      setRows(data.items || []);
    } catch (_e) { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { if (isHr) loadStatus(); }, [isHr]);
  useEffect(() => { if (isHr) loadRows(); /* eslint-disable-next-line */ }, [tab, isHr]);

  if (!isHr) return null;

  const syncEmployees = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/team/simplepay/sync");
      toast.success(`Synced ${data.fetched} SimplePay employees (${data.created} new).`);
      await loadStatus();
      await loadRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed.");
    } finally { setSyncing(false); }
  };

  const syncPayslips = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/team/simplepay/payslips/sync");
      toast.success(`Pulled ${data.fetched || 0} payslips for ${data.employees || 0} employees.`);
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payslip sync failed.");
    } finally { setSyncing(false); }
  };

  const openPicker = async (spid) => {
    try {
      const { data } = await api.get("/team/simplepay/unlinked-users");
      setPicker({ spid, options: data.items || [], filter: "" });
    } catch (_e) { /* noop */ }
  };

  const accept = async (spid, userId) => {
    try {
      await api.post(`/team/simplepay/matches/${spid}/accept`, { user_id: userId });
      toast.success("Linked.");
      setPicker(null);
      await loadStatus();
      await loadRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link failed.");
    }
  };

  const ignore = async (spid) => {
    try {
      await api.post(`/team/simplepay/matches/${spid}/ignore`, { reason: "Not a SalesFlow user" });
      toast.message("Marked as ignored.");
      await loadStatus();
      await loadRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ignore failed.");
    }
  };

  const unlink = async (spid) => {
    if (!confirm("Unlink this SimplePay employee from the SalesFlow user?")) return;
    try {
      await api.post(`/team/simplepay/matches/${spid}/unlink`);
      toast.success("Unlinked.");
      await loadStatus();
      await loadRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unlink failed.");
    }
  };

  const totals = status?.totals || {};

  return (
    <div className="space-y-4" data-testid="simplepay-sync-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/team" className="hv-btn hv-btn-ghost text-xs" data-testid="sp-back">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to Team
        </Link>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#1FB6C1] inline-flex items-center gap-1">
          <CloudDownload className="w-3 h-3" /> SimplePay sync
        </div>
        <span className="w-12" />
      </div>

      {/* Status + actions */}
      <div className="hv-card p-4 sm:p-5">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <div className="font-display text-lg font-bold">Match SimplePay → SalesFlow</div>
            <p className="text-xs text-[#a0aabf] mt-0.5">
              Pull every employee + their history from SimplePay client <span className="font-mono text-[#d7dde8]">#{status?.client_id || "—"}</span>.
              Bind each one to a SalesFlow user so payslips, ID number and tax data only show up on the right person's profile.
            </p>
            <div className="text-[11px] text-[#647087] mt-1">
              {status?.last_employee_sync ? (
                <>Last employee sync: {new Date(status.last_employee_sync.created_at).toLocaleString("en-ZA")} ({status.last_employee_sync.fetched} records)</>
              ) : <>Never synced yet.</>}
              {" · "}
              {status?.last_payslip_sync ? (
                <>Last payslip sync: {new Date(status.last_payslip_sync.created_at).toLocaleString("en-ZA")} ({status.last_payslip_sync.fetched} payslips)</>
              ) : <>No payslips pulled yet.</>}
            </div>
          </div>
          <button onClick={syncEmployees} disabled={syncing} className="hv-btn hv-btn-primary text-xs disabled:opacity-50" data-testid="sp-sync-employees">
            {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Pull employees
          </button>
          <button onClick={syncPayslips} disabled={syncing || !totals.matched} className="hv-btn hv-btn-ghost text-xs disabled:opacity-50" data-testid="sp-sync-payslips" title={totals.matched ? "" : "Match employees first"}>
            {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CloudDownload className="w-3.5 h-3.5" />} Pull payslips
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
          <Stat label="Total in SimplePay" value={totals.simplepay_total || 0} tone="default" />
          <Stat label="Matched" value={totals.matched || 0} tone="ok" />
          <Stat label="Unmatched" value={totals.unmatched || 0} tone="warn" />
          <Stat label="Ignored" value={totals.ignored || 0} tone="muted" />
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1">
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => setTab(t.value)}
            className={`text-[11px] px-2.5 py-1 rounded-full border ${
              tab === t.value
                ? "bg-[#1FB6C1] text-[#0a0b0e] border-[#1FB6C1]"
                : "border-white/15 text-[#a0aabf] hover:bg-white/5"
            }`}
            data-testid={t.testId}
          >
            {t.label}
            {t.value === "unmatched" && totals.unmatched ? ` (${totals.unmatched})` : ""}
            {t.value === "matched" && totals.matched ? ` (${totals.matched})` : ""}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-16 bg-white/[0.04] rounded animate-pulse" />)}
        </div>
      ) : rows.length === 0 ? (
        <div className="hv-card p-8 text-center text-sm text-[#647087]" data-testid="sp-empty">
          {tab === "unmatched" ? "Everything matched — nice." : tab === "matched" ? "No matched employees yet." : "No ignored entries."}
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="sp-list">
          {rows.map((r) => (
            <div key={r.simplepay_id} className="hv-card p-3 flex items-center gap-3 flex-wrap" data-testid={`sp-row-${r.simplepay_id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="text-sm text-white">{r.full_name || `SimplePay #${r.simplepay_id}`}</div>
                <div className="text-[10px] text-[#647087]">
                  SimplePay #{r.simplepay_id} · #{r.employee_number || "—"} · {r.job_title || "—"}
                  {r.email ? ` · ${r.email}` : ""}
                </div>
                {r.suggested_user && tab === "unmatched" && (
                  <div className="text-[11px] text-[#1FB6C1] mt-1">
                    Suggested match: <b>{r.suggested_user.name}</b>
                    <span className="text-[#647087]"> ({r.suggested_user.email || r.suggested_user.role})</span>
                    <span className="ml-1 text-[10px] text-[#647087]">
                      via {r.suggested_reason} · {Math.round((r.suggested_confidence || 0) * 100)}%
                    </span>
                  </div>
                )}
                {r.matched_user && (
                  <div className="text-[11px] text-emerald-300 mt-1">
                    Linked to <b>{r.matched_user.name}</b>
                    <span className="text-[#647087]"> ({r.matched_user.email || r.matched_user.role})</span>
                  </div>
                )}
              </div>
              {tab === "unmatched" && (
                <>
                  {r.suggested_user && (r.suggested_confidence ?? 0) >= 0.66 && (
                    <button
                      onClick={() => accept(r.simplepay_id, r.suggested_user.id)}
                      className="hv-btn hv-btn-primary text-xs"
                      data-testid={`sp-accept-${r.simplepay_id}`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" /> Accept
                    </button>
                  )}
                  <button
                    onClick={() => openPicker(r.simplepay_id)}
                    className="hv-btn hv-btn-ghost text-xs"
                    data-testid={`sp-pick-${r.simplepay_id}`}
                  >
                    <Link2 className="w-3.5 h-3.5" /> Pick user
                  </button>
                  <button
                    onClick={() => ignore(r.simplepay_id)}
                    className="hv-btn hv-btn-ghost text-xs text-[#647087]"
                    data-testid={`sp-ignore-${r.simplepay_id}`}
                  >
                    <X className="w-3.5 h-3.5" /> Ignore
                  </button>
                </>
              )}
              {tab === "matched" && (
                <button
                  onClick={() => unlink(r.simplepay_id)}
                  className="hv-btn hv-btn-ghost text-xs text-amber-300"
                  data-testid={`sp-unlink-${r.simplepay_id}`}
                >
                  <Unlink className="w-3.5 h-3.5" /> Unlink
                </button>
              )}
              {tab === "ignored" && (
                <button
                  onClick={() => openPicker(r.simplepay_id)}
                  className="hv-btn hv-btn-ghost text-xs"
                  data-testid={`sp-reconsider-${r.simplepay_id}`}
                >
                  <Link2 className="w-3.5 h-3.5" /> Bind to user
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Picker dialog (full-screen per product rule) */}
      {picker && (
        <PickerDialog
          options={picker.options}
          onClose={() => setPicker(null)}
          onPick={(uid) => accept(picker.spid, uid)}
        />
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const cls = tone === "ok"
    ? "border-emerald-400/40 text-emerald-200"
    : tone === "warn"
    ? "border-amber-400/40 text-amber-200"
    : tone === "muted"
    ? "border-white/10 text-[#647087]"
    : "border-white/10 text-[#d7dde8]";
  return (
    <div className={`text-center border ${cls} rounded p-2`}>
      <div className="text-xl font-bold">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-[#647087]">{label}</div>
    </div>
  );
}

function PickerDialog({ options, onClose, onPick }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return options;
    return options.filter((o) =>
      (o.name || "").toLowerCase().includes(s) ||
      (o.email || "").toLowerCase().includes(s) ||
      (o.role || "").toLowerCase().includes(s)
    );
  }, [q, options]);

  return (
    <div className="fixed inset-0 z-[200] bg-black/85 backdrop-blur flex flex-col" onClick={onClose} data-testid="sp-picker">
      <div
        className="bg-[#0e1015] flex-1 overflow-y-auto px-4 py-5 max-w-xl w-full mx-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <button onClick={onClose} className="hv-btn hv-btn-ghost text-xs" data-testid="sp-picker-back">
            <ChevronLeft className="w-3.5 h-3.5" /> Cancel
          </button>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#1FB6C1] inline-flex items-center gap-1">
            <Users className="w-3 h-3" /> Pick SalesFlow user
          </div>
          <span className="w-12" />
        </div>
        <p className="text-[11px] text-amber-200 mb-3 inline-flex items-center gap-1">
          <ShieldAlert className="w-3 h-3" />
          Only users without an existing SimplePay link are shown. Double-check before binding — this is the link that decides whose payslips and ID number show up on each profile.
        </p>
        <div className="relative mb-3">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#647087]" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, role…"
            className="hv-input text-sm pl-8 pr-3 w-full"
            data-testid="sp-picker-search"
            autoFocus
          />
        </div>
        <div className="space-y-1">
          {filtered.length === 0 && (
            <div className="text-xs text-[#647087] py-4 text-center">No unlinked users match that search.</div>
          )}
          {filtered.map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => onPick(u.id)}
              className="w-full text-left hv-card p-3 hover:border-[#1FB6C1]/40"
              data-testid={`sp-picker-user-${u.id}`}
            >
              <div className="text-sm text-white">{u.name}</div>
              <div className="text-[10px] text-[#647087]">{u.email || "—"} · {u.role}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
